import React, { useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiLoader } from "react-icons/fi";
import { AuthStatusPanel, authButtonClass, formatAuthText } from "./authPageShell";
import { t, useI18n } from "./utils/i18n";

// Matches the API's one-email-a-minute limit (api/emailSendLimits.js).
const RESEND_COOLDOWN_SECONDS = 60;
// A longer wait than this is a block, shown as a message instead of a countdown.
const COUNTDOWN_MAX_SECONDS = 120;

const postAuthJson = async (path, body) => {
  const response = await fetch(process.env.REACT_APP_API_URL + path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const json = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, json };
};

// Sends a fresh verification email to `email`; used after signup, on an
// expired verification link, and when sign-in says the email is unverified.
// `initialCooldown` covers an email the page has only just sent.
export function ResendVerificationButton({ email, primary = false, initialCooldown = 0 }) {
  const [cooldown, setCooldown] = useState(initialCooldown);
  const [isSending, setIsSending] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [result, setResult] = useState(null);
  useI18n();

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    setIsSending(true);
    try {
      const { ok, status, json } = await postAuthJson("/resend-verification-email", { email });
      if (status === 429) {
        const retryAfter = Math.max(1, Number(json.retryAfterSeconds) || RESEND_COOLDOWN_SECONDS);
        if (retryAfter <= COUNTDOWN_MAX_SECONDS) {
          setResult(null);
          setCooldown(retryAfter);
        } else {
          setIsBlocked(true);
          setResult({ tone: "error", text: formatAuthText(json.messageKey || "auth.email.too_many", { hours: Math.ceil(retryAfter / 3600) }) });
        }
        return;
      }
      setResult({
        tone: ok ? "success" : "error",
        text: t(json.messageKey || (ok ? "auth.verify.resent" : "auth.login.server_error")),
      });
      if (ok) setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setResult({ tone: "error", text: t("auth.login.server_error") });
    } finally {
      setIsSending(false);
    }
  };

  const disabled = !email || isSending || isBlocked || cooldown > 0;
  const label = isSending
    ? t("auth.verify.resending")
    : cooldown > 0
      ? formatAuthText("auth.verify.resend_wait", { seconds: cooldown })
      : t("auth.verify.resend");

  return (
    <div>
      <button type="button" onClick={handleResend} disabled={disabled} className={authButtonClass(disabled, !primary)}>
        {label}
      </button>
      {result ? (
        <p
          role="status"
          className={`mt-2 mb-0 text-center text-[0.85rem] leading-[1.5] ${result.tone === "success" ? "text-emerald-300" : "text-[#fecaca]"}`}
        >
          {result.text}
        </p>
      ) : null}
    </div>
  );
}

const VERIFIED_KEYS = new Set(["auth.verify.success", "auth.verify.already_verified"]);

// Checks the emailed verification link (`/verify-email?email=…&token=…`) once
// and shows the outcome, with sign-in or a fresh link as the next step.
export function VerifyEmailStatus({ email, token, onSwitchMode }) {
  const hasLink = Boolean(email && token);
  const [result, setResult] = useState(hasLink ? null : { verified: false, key: "auth.verify.invalid" });
  const startedRef = useRef(false);
  useI18n();

  useEffect(() => {
    // StrictMode runs effects twice; a second call would report "already verified".
    if (!hasLink || startedRef.current) return;
    startedRef.current = true;
    postAuthJson("/verify-email", { email, token })
      .then(({ ok, json }) => {
        const verified = ok && json.success && VERIFIED_KEYS.has(json.messageKey);
        setResult({ verified, key: json.messageKey || "auth.verify.expired" });
      })
      .catch(() => setResult({ verified: false, key: "auth.login.server_error" }));
  }, [hasLink, email, token]);

  const goToSignIn = (
    <button type="button" onClick={() => onSwitchMode("login")} className={authButtonClass(false, !result?.verified)}>
      {t("auth.common.go_to_sign_in")}
    </button>
  );

  if (!result) {
    return (
      <AuthStatusPanel
        icon={FiLoader}
        spinIcon
        title={t("auth.verify.pending_title")}
        description={t("auth.verify.pending_description")}
      />
    );
  }

  if (result.verified) {
    return (
      <AuthStatusPanel
        tone="success"
        icon={FiCheckCircle}
        title={t("auth.verify.success_title")}
        description={t("auth.verify.success_description")}
      >
        {goToSignIn}
      </AuthStatusPanel>
    );
  }

  return (
    <AuthStatusPanel tone="error" icon={FiAlertCircle} title={t("auth.verify.error_title")} description={t(result.key)}>
      {result.key === "auth.verify.expired" && email ? <ResendVerificationButton email={email} primary /> : null}
      {goToSignIn}
    </AuthStatusPanel>
  );
}
